import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PlanCatalogGrid from '../components/PlanCatalogGrid';
import { buildPlanCatalog } from '../utils/planCatalog';
import usePageMetadata from '../utils/usePageMetadata';
import './Billing.css';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState('');

  usePageMetadata({
    title: 'Pricing | The Ad Shop',
    description:
      'Review The Ad Shop plans for publishers of every size. Start on the free tier, upgrade in-app, or contact us for tailored enterprise pricing.',
    canonicalUrl: 'https://theadshop.co/pricing',
    meta: [
      { property: 'og:title', content: 'Pricing | The Ad Shop' },
      {
        property: 'og:description',
        content:
          'Choose the right plan for your monetization workflow. Compare retention, refresh times, and premium features.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/pricing' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Pricing | The Ad Shop' },
      {
        name: 'twitter:description',
        content:
          'Choose the right plan for your monetization workflow. Compare retention, refresh times, and premium features.'
      },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'The Ad Shop Pricing',
      url: 'https://theadshop.co/pricing',
      description:
        'Pricing plans for The Ad Shop unified analytics platform. Compare free, basic, pro, ultimate, and enterprise plans.',
      isPartOf: {
        '@type': 'WebSite',
        name: 'The Ad Shop',
        url: 'https://theadshop.co/',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://theadshop.co/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Pricing',
            item: 'https://theadshop.co/pricing',
          },
        ],
      },
    },
  });

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        setError('');
        const response = await axios.get('/api/billing/plans');
        if (!isMounted) return;
        setPlans(response.data?.plans || []);
      } catch (err) {
        console.error('Failed to load plans:', err);
        if (!isMounted) return;
        setPlans([]);
        setError(err.response?.data?.message || 'Unable to load plan information.');
      } finally {
        if (isMounted) {
          setLoadingPlans(false);
        }
      }
    };

    loadPlans();
    return () => {
      isMounted = false;
    };
  }, []);

  const planList = useMemo(() => buildPlanCatalog(plans), [plans]);

  return (
    <div className="billing-page pricing-page">
      <div className="billing-container">
        <header className="billing-header">
          <h1>Pricing</h1>
          <p className="billing-subtitle">
            Start on the free tier in minutes. Upgrade inside the product when you need higher retention,
            faster refresh times, or priority support. Looking for enterprise? Create a free account and
            reach out—our team tailors pricing to your footprint.
          </p>
          {error && <div className="billing-error">{error}</div>}
        </header>

        <PlanCatalogGrid
          loading={loadingPlans}
          planList={planList}
          mode="pricing"
        />
      </div>
    </div>
  );
};

export default Pricing;


